import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { AnimateHeight } from '../animations';
import { FONT_SIZE_MAP, FONT_WEIGHT_MAP, HEIGHT_MAP, RADIUS_MAP, } from './constants';
import { InputSeparator } from './input-components';
import { baseInputCss } from './input-styles';
import { NumericInput } from './numeric-input';
const syncAutosizeWidth = (input, sizer) => {
    const style = getComputedStyle(input);
    sizer.style.font = style.font;
    sizer.style.letterSpacing = style.letterSpacing;
    sizer.style.textTransform = style.textTransform;
    sizer.style.paddingInlineStart = style.paddingInlineStart;
    sizer.style.paddingInlineEnd = style.paddingInlineEnd;
    // Prefer the rendered value (includes thousand separators from NumericInput).
    sizer.textContent = input.value || input.placeholder || '0';
    const nextWidth = Math.ceil(sizer.getBoundingClientRect().width) + 2;
    input.style.width = `${nextWidth}px`;
    // Prevent caret-at-end scroll from cropping the leading digits.
    input.scrollLeft = 0;
};
export const Input = ({ size = 's', ref, endContent, startContent, error, numeric, className, label, required, withStartSeparator = true, wrapEndContent = true, autosize, ...props }) => {
    const inputRef = React.useRef(null);
    const sizerRef = React.useRef(null);
    const setInputRef = React.useCallback((node) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
    }, [ref]);
    React.useLayoutEffect(() => {
        if (!autosize) {
            if (inputRef.current) {
                inputRef.current.style.width = '';
            }
            return;
        }
        const input = inputRef.current;
        const sizer = sizerRef.current;
        if (!input || !sizer) {
            return;
        }
        syncAutosizeWidth(input, sizer);
    }, [autosize, props.value, props.placeholder, size]);
    const handleChange = React.useCallback((event) => {
        props.onChange?.(event);
        // NumericInput formats after change; remeasure from the rendered value.
        if (autosize) {
            queueMicrotask(() => {
                const input = inputRef.current;
                const sizer = sizerRef.current;
                if (input && sizer) {
                    syncAutosizeWidth(input, sizer);
                }
            });
        }
    }, [autosize, props.onChange]);
    const inputElementProps = {
        ref: setInputRef,
        $autosize: autosize,
        ...props,
        onChange: handleChange,
        'data-size': size,
    };
    const input = React.createElement(numeric ? InputAmount : InputText, inputElementProps);
    const renderEndContent = () => {
        if (!endContent) {
            return undefined;
        }
        if (wrapEndContent) {
            return _jsx(EndContent, { children: endContent });
        }
        return endContent;
    };
    const renderErrorMessage = () => {
        if (typeof error === 'string' && error) {
            return _jsx(ErrorMessage, { children: error });
        }
        return undefined;
    };
    return (_jsxs(Root, { className: className, "data-disabled": props.disabled || props.readOnly, "$size": size, ...props, children: [_jsxs("label", { children: [label ? (_jsxs(Caption, { children: [label, required ? _jsx(Required, { children: "*" }) : undefined] })) : undefined, _jsxs(RootInput, { "$size": size, "$autosize": autosize, "data-error": !!error, children: [startContent ? (_jsxs(StartContent, { "$withSeparator": withStartSeparator, children: [startContent, ' ', withStartSeparator ? _jsx(InputSeparator, {}) : undefined] })) : undefined, input, autosize ? _jsx(AutosizeSizer, { ref: sizerRef, "aria-hidden": true }) : undefined, renderEndContent()] })] }), _jsx(AnimateHeight, { children: renderErrorMessage() })] }));
};
const Root = styled.div `
  --input-height: ${({ $size }) => HEIGHT_MAP[$size]}px;
`;
const Required = styled.span `
  color: var(--ruby-9);
  font-weight: 600;
  font-size: 14px;
`;
const Caption = styled.div `
  font-size: 12px;
  color: var(--text-11);
  margin-bottom: 8px;
`;
const StartContent = styled.div `
  --start-content-spacing: var(--input-start-content-spacing, 10px);

  color: var(--text-11);
  gap: var(--start-content-spacing);
  margin-inline-end: ${({ $withSeparator }) => $withSeparator ? 'var(--start-content-spacing)' : '0px'};

  text-align: left;
  justify-self: start;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1px;
  grid-column: start;

  line-height: 20px;
  height: 100%;

  transition: all 300ms ease;

  ${Root}[data-disabled='true'] & {
    opacity: 0.3;
  }
`;
const RootInput = styled.div `
  font-size: ${({ $size }) => FONT_SIZE_MAP[$size]}px;
  font-weight: ${({ $size }) => FONT_WEIGHT_MAP[$size]};
  border-radius: var(
    --input-border-radius,
    ${({ $size }) => RADIUS_MAP[$size]}px
  );

  display: grid;
  grid-auto-columns: auto;
  grid-template-columns: ${({ $autosize }) => $autosize
    ? '[start] auto [input] auto [end] auto'
    : '[start] auto [input] 1fr [end] auto'};
  border: var(--input-border, 0.5px solid transparent);

  background: var(--input-background, var(--appearanceA-7));

  padding-inline: var(--input-padding-inline, 12px);

  align-items: center;
  height: var(--input-height);

  transition: all 300ms ease;

  &:focus-within {
    background: var(--input-background-active, var(--appearance-1));
    border: var(--input-border-active, 0.5px solid var(--backgroundA-4));

    ${StartContent} {
      color: var(--text-12);
    }
  }

  &[data-error='true'] {
    border: 1px solid var(--error-9);
  }
`;
const AutosizeSizer = styled.span `
  position: absolute;
  visibility: hidden;
  white-space: pre;
  pointer-events: none;
  height: 0;
  overflow: hidden;
`;
const autosizeInputCss = css `
  width: auto;
  min-width: var(--input-autosize-min-width, 1ch);
  overflow: hidden;
`;
const InputAmount = styled(NumericInput) `
  ${baseInputCss}
  grid-column: input;

  ${({ $autosize }) => $autosize && autosizeInputCss}
`;
const InputText = styled.input `
  ${baseInputCss}
  grid-column: input;

  ${({ $autosize }) => $autosize && autosizeInputCss}

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--text-12);
    -webkit-box-shadow: 0 0 0px 1000px var(--appearance-1) inset;
    transition: background-color 5000s ease-in-out 0s;
  }
`;
const EndContent = styled.div `
  color: var(--text-8);

  text-align: right;
  font-weight: 600;
  line-height: 20px;
  justify-self: end;
  grid-column: end;
  display: grid;

  ${Root}[data-disabled='true'] & {
    opacity: 0.3;
  }
`;
const ErrorMessage = styled.div `
  color: var(--error-8);

  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
`;
Input.Separator = InputSeparator;
//# sourceMappingURL=input.js.map